import { useState, useEffect } from 'react';
import { builderApi } from '../api';
import { fmtDate, DateInput } from '../utils';

const STATUS_COLORS = { ACTIVE: '#38a169', IN_PROGRESS: '#805ad5', COMPLETED: '#3182ce', ON_HOLD: '#d69e2e', CANCELLED: '#e53e3e' };
const WORK_STATUS_COLOR = { PENDING: '#718096', IN_PROGRESS: '#3182ce', COMPLETED: '#38a169', ON_HOLD: '#d69e2e' };
const STATUSES = ['ACTIVE', 'IN_PROGRESS', 'ON_HOLD', 'COMPLETED', 'CANCELLED'];
const EMPTY = { name: '', location: '', start_date: '', end_date: '', budget: '', status: 'ACTIVE', notes: '' };

const fmt = n => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtQ = n => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });

const ErrBanner = ({ msg, onClose }) => msg ? (
  <div style={{ background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 8,
    padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
    <span style={{ fontSize: 18 }}>⚠️</span>
    <span style={{ color: '#c53030', fontSize: 13, fontWeight: 600, flex: 1 }}>{msg}</span>
    <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#c53030', cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
  </div>
) : null;

const inv = (fe, k) => fe[k] ? { borderColor: '#e53e3e', background: '#fff5f5', boxShadow: '0 0 0 3px rgba(229,62,62,.15)' } : {};

// ── Stat card ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color, icon }) {
  return (
    <div style={{ background: 'var(--bg-secondary)', borderRadius: 10, padding: '14px 16px',
      borderLeft: `4px solid ${color}`, flex: 1, minWidth: 140 }}>
      <div style={{ fontSize: 22, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginTop: 3 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: '#718096', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Project Summary Panel ──────────────────────────────────────────────────────
function ProjectSummary({ projectId, onBack }) {
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]     = useState('');

  useEffect(() => {
    setLoading(true);
    builderApi.getProjectSummary(projectId)
      .then(setData)
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) return <div style={{ color: '#718096', fontSize: 13, padding: 24 }}>Loading summary…</div>;
  if (err)     return <><ErrBanner msg={err} onClose={() => setErr('')} /><button className="btn btn-outline" onClick={onBack}>← Back</button></>;
  if (!data)   return null;

  const { project: p, materials, work_entries, purchases,
    total_purchased_cost, total_issued_cost, total_remaining_cost,
    total_labour, total_project_cost } = data;

  const budget = Number(p.budget) || 0;
  const budgetUsed = budget > 0 ? Math.round((total_project_cost / budget) * 100) : null;
  const statusColor = STATUS_COLORS[p.status] || '#718096';

  return (
    <div>
      {/* ── Back + header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button className="btn btn-outline" style={{ fontSize: 12, padding: '4px 12px' }} onClick={onBack}>
          ← Back to Projects
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 20, color: 'var(--text-primary)' }}>{p.name}</h2>
            <span style={{ background: statusColor, color: '#fff', padding: '3px 10px',
              borderRadius: 12, fontSize: 11, fontWeight: 700 }}>{p.status?.replace('_', ' ')}</span>
          </div>
          {(p.location || p.start_date) && (
            <div style={{ fontSize: 12, color: '#718096', marginTop: 3 }}>
              {p.location && <span>📍 {p.location}</span>}
              {p.location && p.start_date && <span style={{ margin: '0 8px' }}>·</span>}
              {p.start_date && <span>🗓 {fmtDate(p.start_date)} {p.end_date ? `→ ${fmtDate(p.end_date)}` : ''}</span>}
            </div>
          )}
        </div>
      </div>

      {/* ── Stat cards ── */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard icon="🛒" label="Materials Purchased" value={fmt(total_purchased_cost)}
          sub={`${materials.length} material type${materials.length !== 1 ? 's' : ''}`}
          color="#3182ce" />
        <StatCard icon="🔄" label="Issued to Site" value={fmt(total_issued_cost)}
          sub="consumed from this project"
          color="#d69e2e" />
        <StatCard icon="📦" label="Remaining Value" value={fmt(total_remaining_cost)}
          sub="on-hand inventory value"
          color={total_remaining_cost > 0 ? '#38a169' : '#718096'} />
        <StatCard icon="👷" label="Labour Expenses" value={fmt(total_labour)}
          sub={`work ₹${Number(data.labour_work || 0).toLocaleString('en-IN')} + payments ₹${Number(data.labour_payments || 0).toLocaleString('en-IN')}`}
          color="#805ad5" />
        <StatCard icon="💰" label="Total Project Cost" value={fmt(total_project_cost)}
          sub="materials + labour"
          color="#e53e3e" />
        {budget > 0 && (
          <StatCard icon="📊" label="Budget Utilisation"
            value={`${budgetUsed}%`}
            sub={`Budget: ${fmt(budget)} · ${budgetUsed <= 100 ? fmt(budget - total_project_cost) + ' remaining' : fmt(total_project_cost - budget) + ' over budget'}`}
            color={budgetUsed > 100 ? '#e53e3e' : budgetUsed > 80 ? '#d69e2e' : '#38a169'} />
        )}
      </div>

      {/* ── Progress bar ── */}
      {budget > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#718096', marginBottom: 4 }}>
            <span>Budget utilisation</span>
            <span>{fmt(total_project_cost)} of {fmt(budget)}</span>
          </div>
          <div style={{ height: 10, background: '#e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{
              width: `${Math.min(budgetUsed, 100)}%`, height: '100%', borderRadius: 6,
              background: budgetUsed > 100 ? '#e53e3e' : budgetUsed > 80 ? '#d69e2e' : '#38a169',
              transition: 'width .4s',
            }} />
          </div>
        </div>
      )}

      {/* ── Material breakdown ── */}
      <div className="bld-card" style={{ margin: '0 0 16px' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
          📦 Inventory Breakdown
        </h3>
        {materials.length === 0 ? (
          <div style={{ color: '#718096', fontSize: 13 }}>No materials purchased for this project yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '20%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '14%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Material</th>
                  <th>Category</th>
                  <th>Unit</th>
                  <th style={{ textAlign: 'right' }}>Purchased Qty</th>
                  <th style={{ textAlign: 'right' }}>Issued Qty</th>
                  <th style={{ textAlign: 'right' }}>Remaining Qty</th>
                  <th style={{ textAlign: 'right' }}>Purchase Cost</th>
                  <th style={{ textAlign: 'right' }}>Remaining Value</th>
                </tr>
              </thead>
              <tbody>
                {materials.map((m, i) => {
                  const issuedPct = Number(m.purchased_qty) > 0
                    ? Math.round((Number(m.issued_qty) / Number(m.purchased_qty)) * 100) : 0;
                  return (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{m.material_name}</td>
                      <td style={{ fontSize: 12, color: '#718096' }}>{m.category || '—'}</td>
                      <td>{m.unit || '—'}</td>
                      <td style={{ textAlign: 'right', color: '#3182ce', fontWeight: 600 }}>
                        {fmtQ(m.purchased_qty)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span style={{ color: Number(m.issued_qty) > 0 ? '#d69e2e' : '#a0aec0', fontWeight: 600 }}>
                          {fmtQ(m.issued_qty)}
                        </span>
                        {issuedPct > 0 && (
                          <span style={{ marginLeft: 4, fontSize: 10, color: '#718096' }}>({issuedPct}%)</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700,
                        color: Number(m.remaining_qty) <= 0 ? '#e53e3e' : '#38a169' }}>
                        {fmtQ(m.remaining_qty)}
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmt(m.purchased_cost)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700,
                        color: Number(m.remaining_cost) > 0 ? '#38a169' : '#718096' }}>
                        {fmt(m.remaining_cost)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--bg-secondary)', fontWeight: 700, fontSize: 13 }}>
                  <td colSpan={6} style={{ padding: '6px 12px', textAlign: 'right', color: '#4a5568' }}>Totals</td>
                  <td style={{ textAlign: 'right', color: '#3182ce', padding: '6px 12px' }}>{fmt(total_purchased_cost)}</td>
                  <td style={{ textAlign: 'right', color: '#38a169', padding: '6px 12px' }}>{fmt(total_remaining_cost)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* ── Work entries + Purchases ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        {/* Recent work */}
        <div className="bld-card" style={{ margin: 0 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
            👷 Recent Work Entries
          </h3>
          {work_entries.length === 0 ? (
            <div style={{ color: '#718096', fontSize: 13 }}>No work logged for this project.</div>
          ) : work_entries.map(w => (
            <div key={w.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              padding: '7px 0', borderBottom: '1px solid var(--border-color)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {w.trade}
                </div>
                <div style={{ fontSize: 11, color: '#718096', marginTop: 1 }}>
                  {w.worker_name || '—'} · {fmtDate(w.work_date)}
                </div>
              </div>
              <div style={{ textAlign: 'right', marginLeft: 10, flexShrink: 0 }}>
                <span style={{ background: WORK_STATUS_COLOR[w.status] || '#718096', color: '#fff',
                  padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700 }}>
                  {w.status?.replace('_', ' ')}
                </span>
                {Number(w.labour_charge) > 0 && (
                  <div style={{ fontSize: 11, color: '#805ad5', fontWeight: 700, marginTop: 3 }}>
                    {fmt(w.labour_charge)}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, color: '#718096' }}>Labour (work entries)</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#805ad5' }}>{fmt(data.labour_work)}</span>
          </div>
        </div>

        {/* Recent purchases */}
        <div className="bld-card" style={{ margin: 0 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
            🛒 Recent Purchases
          </h3>
          {purchases.length === 0 ? (
            <div style={{ color: '#718096', fontSize: 13 }}>No purchases linked to this project.</div>
          ) : purchases.map(pu => (
            <div key={pu.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              padding: '7px 0', borderBottom: '1px solid var(--border-color)' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{pu.supplier || 'No supplier'}</div>
                <div style={{ fontSize: 11, color: '#718096', marginTop: 1 }}>
                  {pu.invoice_no
                    ? <span style={{ fontFamily: 'monospace', background: '#ebf8ff', color: '#2b6cb0',
                        padding: '1px 5px', borderRadius: 4, marginRight: 6 }}>{pu.invoice_no}</span>
                    : null}
                  {fmtDate(pu.purchase_date)}
                </div>
              </div>
              <div style={{ fontWeight: 700, color: '#3182ce', fontSize: 13, marginLeft: 10, flexShrink: 0 }}>
                {fmt(pu.computed_gross)}
              </div>
            </div>
          ))}
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, color: '#718096' }}>Total material purchases</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#3182ce' }}>{fmt(total_purchased_cost)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Projects page ─────────────────────────────────────────────────────────
export default function BuilderProjects({ user }) {
  const isOwner = user?.role === 'role_owner';
  const [projects, setProjects]         = useState([]);
  const [form, setForm]                 = useState(EMPTY);
  const [editing, setEditing]           = useState(null);
  const [showForm, setShowForm]         = useState(false);
  const [viewingProject, setViewingProject] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [err, setErr]                   = useState('');
  const [fe, setFe]                     = useState({});

  const load = () => builderApi.getProjects().then(setProjects).catch(e => setErr(e.message)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const validate = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = true;
    setFe(errors);
    if (Object.keys(errors).length > 0) { setErr('Project Name is required'); return false; }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    try {
      if (editing) await builderApi.updateProject(editing, form);
      else await builderApi.createProject(form);
      setShowForm(false); setEditing(null); setForm(EMPTY); setFe({});
      load();
    } catch (e) { setErr(e.message); }
  };

  const del = async id => {
    if (!window.confirm('Delete this project and all its work/issue records?')) return;
    try { await builderApi.deleteProject(id); load(); } catch (e) { setErr(e.message); }
  };

  const startEdit = p => {
    setForm({ name: p.name||'', location: p.location||'', start_date: p.start_date?.slice(0,10)||'',
      end_date: p.end_date?.slice(0,10)||'', budget: p.budget||'', status: p.status||'ACTIVE', notes: p.notes||'' });
    setEditing(p.id); setShowForm(true); setFe({});
  };

  const f = (k, v) => { setForm(prev => ({ ...prev, [k]: v })); if (fe[k]) setFe(p => ({ ...p, [k]: false })); };

  // ── Summary view ──
  if (viewingProject) {
    return (
      <div className="bld-card">
        <ProjectSummary projectId={viewingProject} onBack={() => setViewingProject(null)} />
      </div>
    );
  }

  return (
    <div className="bld-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, color: 'var(--text-primary)' }}>Projects</h2>
        <button className="btn btn-primary" onClick={() => { setForm(EMPTY); setEditing(null); setShowForm(true); setFe({}); setErr(''); }}>
          + New Project
        </button>
      </div>

      <ErrBanner msg={err} onClose={() => setErr('')} />

      {showForm && (
        <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Project Name *</label>
              <input className="form-input" style={inv(fe,'name')} value={form.name}
                onChange={e => f('name', e.target.value)} placeholder="e.g. Green Valley Apartments" />
              {fe.name && <div style={{ color: '#e53e3e', fontSize: 11, marginTop: 3 }}>Required</div>}
            </div>
            <div>
              <label className="form-label">Location</label>
              <input className="form-input" value={form.location} onChange={e => f('location', e.target.value)} placeholder="Site address" />
            </div>
            <div>
              <label className="form-label">Start Date</label>
              <DateInput className="form-input" value={form.start_date} onChange={e => f('start_date', e.target.value)} />
            </div>
            <div>
              <label className="form-label">End Date</label>
              <DateInput className="form-input" value={form.end_date} onChange={e => f('end_date', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Budget (₹)</label>
              <input className="form-input" type="number" value={form.budget} onChange={e => f('budget', e.target.value)} placeholder="0.00" />
            </div>
            <div>
              <label className="form-label">Status</label>
              <select className="form-input" value={form.status} onChange={e => f('status', e.target.value)}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Notes</label>
              <input className="form-input" value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Additional details" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-primary" onClick={save}>{editing ? 'Update' : 'Create'} Project</button>
            <button className="btn btn-outline" onClick={() => { setShowForm(false); setEditing(null); setFe({}); setErr(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ color: '#718096', fontSize: 13 }}>Loading…</div> : (
        projects.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#718096', padding: 40, fontSize: 14 }}>
            No projects yet. Click "+ New Project" to get started.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {projects.map(p => {
              const statusColor = STATUS_COLORS[p.status] || '#718096';
              return (
                <div key={p.id} style={{ background: 'var(--bg-secondary)', borderRadius: 10,
                  padding: '14px 16px', border: '1px solid var(--border-color)',
                  borderLeft: `4px solid ${statusColor}`, display: 'flex', alignItems: 'center', gap: 12 }}>
                  {/* Project info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)',
                        cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => setViewingProject(p.id)}>
                        {p.name}
                      </span>
                      <span style={{ background: statusColor, color: '#fff', padding: '2px 8px',
                        borderRadius: 12, fontSize: 10, fontWeight: 700 }}>
                        {p.status?.replace('_', ' ')}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: '#718096', marginTop: 3, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      {p.location && <span>📍 {p.location}</span>}
                      {p.start_date && <span>🗓 {fmtDate(p.start_date)}{p.end_date ? ` → ${fmtDate(p.end_date)}` : ''}</span>}
                      {p.budget ? <span>💰 Budget: ₹{Number(p.budget).toLocaleString('en-IN')}</span> : null}
                    </div>
                    {p.notes && <div style={{ fontSize: 11, color: '#a0aec0', marginTop: 2 }}>{p.notes}</div>}
                  </div>
                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button className="btn btn-primary" style={{ fontSize: 12, padding: '4px 12px' }}
                      onClick={() => setViewingProject(p.id)}>
                      📊 Summary
                    </button>
                    <button className="btn btn-outline" style={{ fontSize: 12, padding: '4px 10px' }}
                      onClick={() => startEdit(p)}>Edit</button>
                    {isOwner && (
                      <button className="btn" style={{ background: '#fed7d7', color: '#c53030', padding: '4px 10px', fontSize: 12 }}
                        onClick={() => del(p.id)}>Del</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
